import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { formatDateTime, prettify } from "@/lib/format";
import { StatusBadge } from "@/components/StatusBadge";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { RefreshCw, Search, RotateCcw, Play, Plus, Trash2, ShieldBan, Send, AlertCircle, MailCheck } from "lucide-react";
import { toast } from "sonner";

const STAT_CARDS = [
  { key: "total", label: "Total", tint: "rgba(28,67,88,0.10)", color: "#1C4358" },
  { key: "queued", label: "Queued", tint: "rgba(196,200,155,0.35)", color: "#5A5E2E" },
  { key: "sent", label: "Sent", tint: "rgba(155,185,206,0.25)", color: "#1C4358" },
  { key: "delivered", label: "Delivered", tint: "rgba(47,125,90,0.12)", color: "#256B4B" },
  { key: "bounced", label: "Bounced", tint: "rgba(156,30,34,0.10)", color: "#9C1E22" },
  { key: "complained", label: "Complaints", tint: "rgba(156,30,34,0.10)", color: "#9C1E22" },
  { key: "failed", label: "Failed", tint: "rgba(156,30,34,0.10)", color: "#9C1E22" },
  { key: "skipped", label: "Skipped", tint: "rgba(75,85,99,0.10)", color: "#4B5563" },
  { key: "suppressed", label: "Suppressed", tint: "rgba(75,85,99,0.10)", color: "#4B5563" },
];

const OUTBOX_STATUSES = ["all", "queued", "retry", "sending", "sent", "delivered", "bounced", "complained", "failed", "skipped", "delayed"];

export default function EmailActivity() {
  const [stats, setStats] = useState(null);

  // Outbox state
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("all");
  const [q, setQ] = useState("");
  const [processing, setProcessing] = useState(false);
  const [retryingId, setRetryingId] = useState(null);

  // Suppression state
  const [supps, setSupps] = useState([]);
  const [suppLoading, setSuppLoading] = useState(true);
  const [suppQ, setSuppQ] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [newSupp, setNewSupp] = useState({ email: "", reason: "manual" });

  // Test-send state
  const [testOpen, setTestOpen] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [testing, setTesting] = useState(false);

  const loadStats = useCallback(async () => {
    try { const { data } = await api.get("/email/stats"); setStats(data); } catch { /* noop */ }
  }, []);

  const loadOutbox = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 200 };
      if (status !== "all") params.status = status;
      if (q) params.q = q;
      const { data } = await api.get("/email/outbox", { params });
      setItems(data.items || []);
    } finally { setLoading(false); }
  }, [status, q]);

  const loadSupps = useCallback(async () => {
    setSuppLoading(true);
    try {
      const params = {}; if (suppQ) params.q = suppQ;
      const { data } = await api.get("/email/suppressions", { params });
      setSupps(data.items || []);
    } finally { setSuppLoading(false); }
  }, [suppQ]);

  useEffect(() => { loadStats(); }, [loadStats]);
  useEffect(() => { loadOutbox(); }, [loadOutbox]);
  useEffect(() => { loadSupps(); }, [loadSupps]);

  const processNow = async () => {
    setProcessing(true);
    try {
      const { data } = await api.post("/email/process");
      if (!data.configured) toast.warning("Email provider is not configured — items stay queued.");
      else toast.success(`Queue drained — ${data.sent} sent, ${data.failed} failed, ${data.retry} retrying`);
      loadStats(); loadOutbox();
    } catch { toast.error("Could not process the queue"); }
    finally { setProcessing(false); }
  };

  const retry = async (item) => {
    setRetryingId(item.id);
    try {
      await api.post(`/email/outbox/${item.id}/retry`);
      toast.success("Re-queued for delivery");
      loadStats(); loadOutbox();
    } catch { toast.error("Could not retry"); }
    finally { setRetryingId(null); }
  };

  const addSupp = async () => {
    if (!newSupp.email.trim()) return toast.error("Email is required");
    try {
      await api.post("/email/suppressions", newSupp);
      toast.success("Email suppressed");
      setAddOpen(false); setNewSupp({ email: "", reason: "manual" });
      loadStats(); loadSupps();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not suppress"); }
  };

  const removeSupp = async (s) => {
    try {
      await api.delete(`/email/suppressions/${encodeURIComponent(s.email)}`);
      toast.success("Suppression removed");
      loadStats(); loadSupps();
    } catch { toast.error("Could not remove"); }
  };

  const sendTest = async () => {
    if (!testEmail.trim()) return toast.error("Enter a recipient email");
    setTesting(true);
    try {
      const { data } = await api.post("/email/test-send", { to_email: testEmail.trim() });
      const st = data?.outbox?.status;
      if (st === "sent" || st === "delivered") toast.success(`Test email sent to ${testEmail}`);
      else if (st === "skipped") toast.warning(`Skipped — ${data?.outbox?.skip_reason || "recipient is suppressed"}`);
      else toast.error(`Send ${st || "failed"}${data?.outbox?.last_error ? ` — ${data.outbox.last_error}` : ""}`);
      setTestOpen(false); setTestEmail("");
      loadStats(); loadOutbox();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not send test email"); }
    finally { setTesting(false); }
  };

  const providerName = stats?.provider ? prettify(stats.provider) : "—";
  const configured = stats?.configured;

  return (
    <div className="space-y-5" data-testid="email-activity-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-2xl font-bold text-[#252221]">Email Activity</h1>
          <p className="text-sm text-muted-foreground">Monitor deliverability, retries, bounces and your suppression list.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <span
            data-testid="email-provider-badge"
            className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold border"
            style={configured
              ? { backgroundColor: "rgba(47,125,90,0.12)", color: "#256B4B", borderColor: "rgba(47,125,90,0.28)" }
              : { backgroundColor: "rgba(183,121,31,0.14)", color: "#9A6516", borderColor: "rgba(183,121,31,0.30)" }}
          >
            <Send size={13} /> {providerName} · {configured ? "Configured" : "Not configured"}
          </span>
          <Button variant="outline" onClick={() => { loadStats(); loadOutbox(); loadSupps(); }} data-testid="email-refresh-button">
            <RefreshCw size={15} className="mr-1.5" /> Refresh
          </Button>
          <Button variant="outline" onClick={() => setTestOpen(true)} data-testid="email-test-open-button">
            <MailCheck size={15} className="mr-1.5" /> Send test email
          </Button>
          <Button onClick={processNow} disabled={processing} className="text-white" style={{ backgroundColor: "var(--mmm-red)" }} data-testid="email-process-button">
            <Play size={15} className={`mr-1.5 ${processing ? "animate-pulse" : ""}`} /> Process queue now
          </Button>
        </div>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-9 gap-2.5" data-testid="email-stat-cards">
        {STAT_CARDS.map((c) => (
          <Card key={c.key} className="border-[#E6EDF2] p-3" style={{ boxShadow: "var(--mmm-shadow-sm)" }} data-testid={`email-stat-${c.key}`}>
            <div className="text-[11px] font-semibold uppercase tracking-wide" style={{ color: c.color }}>{c.label}</div>
            <div className="mt-1 font-display text-2xl font-bold" style={{ color: c.color }}>
              {stats ? (stats[c.key] ?? 0) : <Skeleton className="h-7 w-10" />}
            </div>
          </Card>
        ))}
      </div>

      {!configured && (
        <div className="flex items-start gap-2 rounded-lg border p-3 text-sm" style={{ backgroundColor: "rgba(183,121,31,0.10)", borderColor: "rgba(183,121,31,0.30)", color: "#7A5312" }} data-testid="email-provider-warning">
          <AlertCircle size={16} className="mt-0.5 shrink-0" />
          <span>The email provider isn’t configured yet. Queued emails will remain pending until a provider API key is set.</span>
        </div>
      )}

      <Tabs defaultValue="outbox" className="w-full">
        <TabsList data-testid="email-tabs">
          <TabsTrigger value="outbox" data-testid="email-tab-outbox">Outbox</TabsTrigger>
          <TabsTrigger value="suppressions" data-testid="email-tab-suppressions">Suppressions</TabsTrigger>
        </TabsList>

        {/* ---------------- Outbox ---------------- */}
        <TabsContent value="outbox" className="space-y-4 mt-4">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative">
              <Search size={15} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search recipient or subject" className="pl-8 w-[260px]" data-testid="email-outbox-search" />
            </div>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger className="w-[170px]" data-testid="email-outbox-status-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                {OUTBOX_STATUSES.map((s) => <SelectItem key={s} value={s}>{s === "all" ? "All statuses" : prettify(s)}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <Card className="border-[#E6EDF2] overflow-hidden" style={{ boxShadow: "var(--mmm-shadow-sm)" }}>
            {loading ? <div className="p-4 space-y-3">{[...Array(6)].map((_, i) => <Skeleton key={i} className="h-10" />)}</div> :
              items.length === 0 ? <div className="p-10 text-center text-muted-foreground" data-testid="email-outbox-empty">No emails match this view yet.</div> : (
              <div className="overflow-x-auto mmm-scroll">
                <Table data-testid="email-outbox-table">
                  <TableHeader><TableRow className="hover:bg-transparent">
                    <TableHead className="text-xs font-semibold text-[#1C4358]/80">Recipient</TableHead>
                    <TableHead className="text-xs font-semibold text-[#1C4358]/80">Subject</TableHead>
                    <TableHead className="text-xs font-semibold text-[#1C4358]/80">Source</TableHead>
                    <TableHead className="text-xs font-semibold text-[#1C4358]/80">Status</TableHead>
                    <TableHead className="text-xs font-semibold text-[#1C4358]/80">Attempts</TableHead>
                    <TableHead className="text-xs font-semibold text-[#1C4358]/80">Updated</TableHead>
                    <TableHead></TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {items.map((m) => (
                      <TableRow key={m.id} className="hover:bg-[rgba(155,185,206,0.10)]" data-testid="email-outbox-row">
                        <TableCell className="font-medium text-[#252221] max-w-[200px] truncate">{m.to_email}</TableCell>
                        <TableCell className="text-sm max-w-[240px] truncate">{m.subject || "—"}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">{prettify(m.source || "")}</TableCell>
                        <TableCell>
                          <StatusBadge value={m.status} />
                          {m.last_error && <div className="mt-1 text-[11px] text-[#9C1E22] max-w-[220px] truncate" title={m.last_error}>{m.last_error}</div>}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">{m.attempts ?? 0}/{m.max_attempts ?? 4}</TableCell>
                        <TableCell className="text-sm text-muted-foreground whitespace-nowrap">{formatDateTime(m.updated_at)}</TableCell>
                        <TableCell>
                          <div className="flex items-center justify-end">
                            {["failed", "bounced", "skipped"].includes(m.status) && (
                              <button onClick={() => retry(m)} disabled={retryingId === m.id} className="p-2 rounded-lg text-[#1C4358] hover:bg-[rgba(28,67,88,0.08)]" title="Re-queue" data-testid="email-retry-button">
                                <RotateCcw size={15} className={retryingId === m.id ? "animate-spin" : ""} />
                              </button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </Card>
        </TabsContent>

        {/* ---------------- Suppressions ---------------- */}
        <TabsContent value="suppressions" className="space-y-4 mt-4">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="relative">
              <Search size={15} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input value={suppQ} onChange={(e) => setSuppQ(e.target.value)} placeholder="Search email" className="pl-8 w-[260px]" data-testid="email-supp-search" />
            </div>
            <Button onClick={() => setAddOpen(true)} className="text-white" style={{ backgroundColor: "var(--mmm-red)" }} data-testid="email-supp-add-button">
              <Plus size={15} className="mr-1.5" /> Suppress email
            </Button>
          </div>

          <Card className="border-[#E6EDF2] overflow-hidden" style={{ boxShadow: "var(--mmm-shadow-sm)" }}>
            {suppLoading ? <div className="p-4 space-y-3">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-10" />)}</div> :
              supps.length === 0 ? <div className="p-10 text-center text-muted-foreground" data-testid="email-supp-empty">No suppressed addresses. Bounces and complaints are added here automatically.</div> : (
              <div className="overflow-x-auto mmm-scroll">
                <Table data-testid="email-supp-table">
                  <TableHeader><TableRow className="hover:bg-transparent">
                    <TableHead className="text-xs font-semibold text-[#1C4358]/80">Email</TableHead>
                    <TableHead className="text-xs font-semibold text-[#1C4358]/80">Reason</TableHead>
                    <TableHead className="text-xs font-semibold text-[#1C4358]/80">Added</TableHead>
                    <TableHead></TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {supps.map((s) => (
                      <TableRow key={s.id} className="hover:bg-[rgba(155,185,206,0.10)]" data-testid="email-supp-row">
                        <TableCell className="font-medium text-[#252221]"><ShieldBan size={14} className="inline mr-1.5 text-[#9C1E22]" />{s.email}</TableCell>
                        <TableCell><StatusBadge value={s.reason} /></TableCell>
                        <TableCell className="text-sm text-muted-foreground whitespace-nowrap">{formatDateTime(s.created_at)}</TableCell>
                        <TableCell>
                          <div className="flex items-center justify-end">
                            <button onClick={() => removeSupp(s)} className="p-2 rounded-lg text-[#9C1E22] hover:bg-[rgba(156,30,34,0.08)]" title="Remove suppression" data-testid="email-supp-remove"><Trash2 size={15} /></button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={testOpen} onOpenChange={setTestOpen}>
        <DialogContent className="sm:max-w-md" data-testid="email-test-dialog">
          <DialogHeader><DialogTitle>Send a test email</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-sm">Recipient</Label><Input value={testEmail} onChange={(e) => setTestEmail(e.target.value)} placeholder="you@example.com" data-testid="email-test-input" /></div>
            <p className="text-xs text-muted-foreground">A branded test message is sent immediately from your configured sender to verify live delivery.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTestOpen(false)}>Cancel</Button>
            <Button onClick={sendTest} disabled={testing} className="text-white" style={{ backgroundColor: "var(--mmm-red)" }} data-testid="email-test-send-button">
              {testing ? "Sending…" : "Send test"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="sm:max-w-md" data-testid="email-supp-dialog">
          <DialogHeader><DialogTitle>Suppress an email</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-sm">Email</Label><Input value={newSupp.email} onChange={(e) => setNewSupp({ ...newSupp, email: e.target.value })} placeholder="name@example.com" data-testid="email-supp-email-input" /></div>
            <div>
              <Label className="text-sm">Reason</Label>
              <Select value={newSupp.reason} onValueChange={(v) => setNewSupp({ ...newSupp, reason: v })}>
                <SelectTrigger data-testid="email-supp-reason-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="manual">Manual</SelectItem>
                  <SelectItem value="bounce">Bounce</SelectItem>
                  <SelectItem value="complaint">Complaint</SelectItem>
                  <SelectItem value="unsubscribed">Unsubscribed</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <p className="text-xs text-muted-foreground">Suppressed addresses are skipped by the sending worker and marked unsubscribed.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)}>Cancel</Button>
            <Button onClick={addSupp} className="text-white" style={{ backgroundColor: "var(--mmm-red)" }} data-testid="email-supp-save-button">Suppress</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
